import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import { TextField } from '@woovi/form';
import {
  type FetchKeyProp,
  withRelayBoundary,
  useMutationCallbacks,
} from '@woovi/relay';
import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { ActionButton } from '@woovi/ui';

import type { PaymentActiveITPBanks_charge$key } from './__generated__/PaymentActiveITPBanks_charge.graphql.ts';
import PaymentActiveITPBanksList from './PaymentActiveITPBanksList.tsx';
import type { ChargeAddPaymentMethodMutation } from '../../activeWaitingPaymentMethod/mutations/__generated__/ChargeAddPaymentMethodMutation.graphql.ts';
import { ChargeAddPaymentMethod } from '../../activeWaitingPaymentMethod/mutations/ChargeAddPaymentMethodMutation.tsx';
import usePaymentContext from '../../context/usePaymentContext.tsx';

import type { ReactNode } from 'react';

type Values = {
  search: string;
  bank?: {
    providerBankId: string;
    providerParticipantId: string;
  };
};

type PaymentActiveITPBanksProps = {
  charge: PaymentActiveITPBanks_charge$key;
} & FetchKeyProp;

const PaymentActiveITPBanks = ({
  fetchKey,
  ...props
}: PaymentActiveITPBanksProps): ReactNode => {
  const { t } = useTranslation();
  const { setRenderITPBanks } = usePaymentContext();
  const theme = useTheme();

  const charge = useFragment<PaymentActiveITPBanks_charge$key>(
    graphql`
      fragment PaymentActiveITPBanks_charge on Charge {
        id
        ...PaymentActiveITPBanksList_charge
      }
    `,
    props.charge,
  );

  const [chargeAddPaymentMethod, isPending] =
    useMutationCallbacks<ChargeAddPaymentMethodMutation>({
      name: 'ChargeAddPaymentMethod',
      mutation: ChargeAddPaymentMethod,
      afterCompleted: (response) => {
        const paymentMethod =
          response.ChargeAddPaymentMethod?.charge?.paymentMethods?.find(
            (paymentMethod) =>
              paymentMethod?.method === PAYMENT_METHOD_TYPE.ITP,
          );

        window.location.href = paymentMethod?.authorizationUrl;
      },
    });

  const onSubmit = (values: Values) => {
    chargeAddPaymentMethod({
      variables: {
        input: {
          chargeId: charge.id,
          paymentMethodType: PAYMENT_METHOD_TYPE.ITP,
          paymentMethodITPConfig: {
            providerBankId: values.bank?.providerBankId,
            providerParticipantId: values.bank?.providerParticipantId,
          },
        },
      },
    });
  };

  const formikbag = useFormik({
    initialValues: {
      search: '',
      bank: undefined,
    },
    validationSchema: yup.object().shape({
      search: yup.string(),
      bank: yup
        .object()
        .shape({
          providerBankId: yup.string().required(),
          providerParticipantId: yup.string().required(),
        })
        .defined()
        .required(),
    }),
    validateOnMount: true,
    onSubmit,
  });

  const { handleSubmit, isValid } = formikbag;

  return (
    <>
      <Box sx={{ position: 'absolute', top: theme.spacing(2), left: 0 }}>
        <ActionButton
          color='secondary'
          variant='text'
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => setRenderITPBanks(false)}
        >
          {t('Back')}
        </ActionButton>
      </Box>
      <FormikProvider value={formikbag}>
        <Stack spacing={3} sx={{ alignItems: 'center' }}>
          <Typography
            component='h1'
            variant='h4'
            fontWeight={theme.typography.fontWeightBold}
          >
            {t('Select your Bank')}
          </Typography>
          <TextField
            name='search'
            placeholder={`${t('Search')}...`}
            disabled={isPending}
            fullWidth
          />
          <PaymentActiveITPBanksList charge={charge} isPending={isPending} />

          <ActionButton
            onClick={handleSubmit}
            loading={isPending}
            disabled={isPending || !isValid}
            fullWidth
          >
            {t('Next')}
          </ActionButton>
        </Stack>
      </FormikProvider>
    </>
  );
};

export default withRelayBoundary(PaymentActiveITPBanks);
